import React, { useState, useEffect, useRef } from 'react';
import { useHistory, useLocation, Link } from 'react-router-dom';
import AppNavbar from '../Shared/AppNavbar';
import Card from '../ui/Card';
import Field from '../ui/Field';
import Button from '../ui/Button';
import Select from '../ui/Select';
import DateInput from '../ui/DateInput';
import IconBadge from '../ui/IconBadge';
import Icon from '../ui/Icon';
import Spinner from '../ui/Spinner';
import Tabs from '../ui/Tabs';
import StepTracker from '../ui/StepTracker';
import InvestigationField from './InvestigationField';
import { ANTENATAL_TABS } from './ViewAnteNatal';
import '../../styles/caseForms.css';
import { useParams } from 'react-router-dom';
import axios from 'axios';
import { getAuthHeader } from '../../utils/auth';
import { API_BASE, apiFetch } from '../../utils/api';

// Standard due-date rule (Naegele's): 40 weeks = 280 days after the first day
// of the last menstrual period. UTC arithmetic so DST or the browser's
// timezone can never shift the result by a day. Anything that isn't a clean
// yyyy-mm-dd date gives '' rather than a wrong date.
const EDD_OFFSET_DAYS = 280;
const eddFromLmp = (lmp) => {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(lmp || '')) return '';
  const [y, m, d] = lmp.split('-').map(Number);
  const start = new Date(Date.UTC(y, m - 1, d));
  if (start.getUTCMonth() !== m - 1 || start.getUTCDate() !== d) return '';
  return new Date(Date.UTC(y, m - 1, d + EDD_OFFSET_DAYS)).toISOString().slice(0, 10);
};

const BLANK_INVESTIGATIONS = {
  bloodInvestigation: { details: '', documents: [] },
  urineInvestigation: { details: '', documents: [] },
  ultrasoundInvestigation: { details: '', documents: [] },
  xrayInvestigation: { details: '', documents: [] },
};

const BLANK_DETAILS = (patientId) => ({
  patientId,
  obstetricHistory: { gravida: '', para: '', abortus: '', living: '' },
  LMP: '',
  expectedDateOfDelivery: '',
  specificHistory: {
    pregnancyComplications: '',
    previousDeliveryBy: ''
  },
  medicalComplications: {
    heartDisease: '',
    lungDisease: '',
    liverDisease: '',
    GIT: '',
    Kidney: '',
    SpineProblem: '',
    Others: ''
  },
  investigations: JSON.parse(JSON.stringify(BLANK_INVESTIGATIONS)),
  treatments: '',
});

// Maps a populated Document object (from the backend) into the shape
// InvestigationField already knows how to display ({name, ...}) - existing
// docs are tagged `existing: true` so handleSubmit knows to send their id
// back rather than try to re-upload them (the update endpoint doesn't
// accept file uploads at all, only create does).
const mapExistingDocs = (docs) => (docs || []).map((doc) => ({
  _id: doc._id,
  name: doc.filename,
  existing: true,
}));

const AntenatalDetailsForm = () => {
  const { patientId } = useParams();
  const history = useHistory();
  const location = useLocation();
  const formRef = useRef(null);
  // Errors used to only go to console.error, so a failed submit (missing
  // obstetric history, or a genuine backend rejection) looked from the
  // user's side like clicking Submit did nothing at all - caught live,
  // this is what actually surfaces it now.
  const [error, setError] = useState(null);
  const [saving, setSaving] = useState(false);
  const errorBannerRef = useRef(null);

  // The submit button sits at the bottom of a long form - a validation
  // error rendered up at the top (next to the heading) is invisible unless
  // something scrolls it into view, same problem the browser's own native
  // "please fill out this field" tooltip solves for free on plain inputs.
  useEffect(() => {
    if (error && errorBannerRef.current) {
      errorBannerRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [error]);

  // Real gap found live: a patient with an AnteNatal case type but no
  // submitted case yet (e.g. the original submit silently failed) had no
  // way back into this form - "View AnteNatal Form" only ever linked to
  // the read-only view, which 404s if nothing was ever saved, with no
  // fallback. This form now does double duty: loads any existing case for
  // this patientId on mount and switches into edit mode if one exists,
  // otherwise behaves exactly as the plain create form always has.
  const [caseId, setCaseId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [antenatalDetails, setAntenatalDetails] = useState(() => BLANK_DETAILS(patientId));
  // True once the due date was entered or changed by hand (or saved earlier
  // with a date that differs from LMP + 40 weeks, e.g. revised after a dating
  // scan) - from then on changing the LMP must not overwrite it.
  const eddEdited = useRef(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch(`${API_BASE}/api/antenatal/getByPatientId?patientId=${patientId}`, { headers: getAuthHeader() })
      .then((res) => (res.status === 404 ? null : res.json()))
      .then((json) => {
        if (cancelled || !json?.data) return;
        const existing = json.data;
        const savedEdd = existing.expectedDateOfDelivery ? existing.expectedDateOfDelivery.slice(0, 10) : '';
        eddEdited.current = Boolean(savedEdd) && savedEdd !== eddFromLmp(existing.LMP);
        setCaseId(existing.caseId);
        setAntenatalDetails({
          patientId,
          obstetricHistory: {
            gravida: existing.obstetricHistory?.gravida ?? 0,
            para: existing.obstetricHistory?.para ?? 0,
            abortus: existing.obstetricHistory?.abortus ?? 0,
            living: existing.obstetricHistory?.living ?? 0,
          },
          LMP: existing.LMP || '',
          expectedDateOfDelivery: existing.expectedDateOfDelivery ? existing.expectedDateOfDelivery.slice(0, 10) : '',
          specificHistory: {
            pregnancyComplications: existing.specificHistory?.pregnancyComplications || '',
            previousDeliveryBy: existing.specificHistory?.previousDeliveryBy || '',
          },
          medicalComplications: {
            heartDisease: existing.medicalComplications?.heartDisease || '',
            lungDisease: existing.medicalComplications?.lungDisease || '',
            liverDisease: existing.medicalComplications?.liverDisease || '',
            GIT: existing.medicalComplications?.GIT || '',
            Kidney: existing.medicalComplications?.Kidney || '',
            SpineProblem: existing.medicalComplications?.SpineProblem || '',
            Others: existing.medicalComplications?.Others || '',
          },
          investigations: {
            bloodInvestigation: {
              details: existing.investigations?.bloodInvestigation?.details || '',
              documents: mapExistingDocs(existing.investigations?.bloodInvestigation?.documents),
            },
            urineInvestigation: {
              details: existing.investigations?.urineInvestigation?.details || '',
              documents: mapExistingDocs(existing.investigations?.urineInvestigation?.documents),
            },
            ultrasoundInvestigation: {
              details: existing.investigations?.ultrasoundInvestigation?.details || '',
              documents: mapExistingDocs(existing.investigations?.ultrasoundInvestigation?.documents),
            },
            xrayInvestigation: {
              details: existing.investigations?.xrayInvestigation?.details || '',
              documents: mapExistingDocs(existing.investigations?.xrayInvestigation?.documents),
            },
          },
          treatments: existing.treatments || '',
        });
      })
      .catch((err) => console.error('Error checking for an existing antenatal case:', err))
      .finally(() => { if (!cancelled) setLoading(false); });
    return () => { cancelled = true; };
  }, [patientId]);

  const isEditMode = caseId !== null;
  // Real bug this fixes: Edit (a real page navigation from ViewAnteNatal.js,
  // not an in-place swap) used to always land here at Obstetric History
  // regardless of which tab was open on the read view. ViewAnteNatal.js's
  // Edit link now carries that tab via route state (location.state), since
  // there's no component to pass a prop through across a navigation.
  const [editTab, setEditTab] = useState(location.state?.initialTab || 'obstetric');

  // Create mode is a guided step wizard instead, same reasoning and same
  // reportValidity()-scoped-to-the-mounted-step trick as AddPatient.js's
  // own wizard - see its comment for the full explanation. Nothing here has
  // a Select-based required field (AnteNatal's only required inputs are
  // plain/DateInput, both real native form controls), so unlike
  // AddPatientForm's Case Type there's no extra manual check needed on top
  // of reportValidity().
  const [wizardStep, setWizardStep] = useState('obstetric');
  const [furthestStep, setFurthestStep] = useState(0);

  const activeSection = isEditMode ? editTab : wizardStep;
  const showSection = (tabKey) => activeSection === tabKey;

  const wizardStepIndex = ANTENATAL_TABS.findIndex((s) => s.key === wizardStep);
  const isLastWizardStep = wizardStepIndex === ANTENATAL_TABS.length - 1;

  const goNext = () => {
    if (formRef.current && !formRef.current.reportValidity()) return;
    setError(null);
    const nextIndex = wizardStepIndex + 1;
    setFurthestStep((f) => Math.max(f, nextIndex));
    setWizardStep(ANTENATAL_TABS[nextIndex].key);
  };

  const goBack = () => {
    if (wizardStepIndex > 0) setWizardStep(ANTENATAL_TABS[wizardStepIndex - 1].key);
  };

  const jumpToStep = (key) => {
    const idx = ANTENATAL_TABS.findIndex((s) => s.key === key);
    if (idx <= furthestStep) setWizardStep(key);
  };

  // Handle form submission
  // event is optional - the wizard's own Submit button calls this directly
  // (see the real-bug comment on the wizard-nav buttons below) rather than
  // relying on native type="submit" form submission.
  const handleSubmit = async (event) => {
    if (event) event.preventDefault();
    setError(null);
    setSaving(true);

    const formData = new FormData();

    // Append non-file data to formData
    formData.append('patientId', antenatalDetails.patientId);
    if (isEditMode) formData.append('caseId', caseId);
    formData.append('obstetricHistory.gravida', Number(antenatalDetails.obstetricHistory.gravida) || 0);
    formData.append('obstetricHistory.para', Number(antenatalDetails.obstetricHistory.para) || 0);
    formData.append('obstetricHistory.abortus', Number(antenatalDetails.obstetricHistory.abortus) || 0);
    formData.append('obstetricHistory.living', Number(antenatalDetails.obstetricHistory.living) || 0);
    formData.append('LMP', antenatalDetails.LMP);
    formData.append('expectedDateOfDelivery', antenatalDetails.expectedDateOfDelivery);
    formData.append('specificHistory.pregnancyComplications', antenatalDetails.specificHistory.pregnancyComplications);
    formData.append('specificHistory.previousDeliveryBy', antenatalDetails.specificHistory.previousDeliveryBy);
    formData.append('medicalComplications.heartDisease', antenatalDetails.medicalComplications.heartDisease);
    formData.append('medicalComplications.liverDisease', antenatalDetails.medicalComplications.liverDisease);
    formData.append('medicalComplications.GIT', antenatalDetails.medicalComplications.GIT);
    formData.append('medicalComplications.Kidney', antenatalDetails.medicalComplications.Kidney);
    formData.append('medicalComplications.SpineProblem', antenatalDetails.medicalComplications.SpineProblem);
    formData.append('medicalComplications.Others', antenatalDetails.medicalComplications.Others);

    // Append files to formData - each document is either a fresh local pick
    // ({name, file}, a real File to upload) or one already on the case from
    // before this edit ({name, _id, existing: true}). Existing ones aren't
    // re-uploaded - their ids go in a parallel "keep list" field instead, so
    // the backend knows which of the case's current documents survived this
    // save (anything not listed there was removed locally) alongside
    // whatever's newly attached here.
    const investigations = antenatalDetails.investigations;
    for (const key in investigations) {
      if (investigations.hasOwnProperty(key)) {
        formData.append(`investigations.${key}.details`, investigations[key].details);
        const keepIds = [];
        investigations[key].documents.forEach((doc) => {
          if (doc.existing) {
            keepIds.push(doc._id);
          } else {
            formData.append(`investigations.${key}.documents`, doc.file);
          }
        });
        if (isEditMode) formData.append(`investigations.${key}.keepDocumentIds`, JSON.stringify(keepIds));
      }
    }
    formData.append('treatments', antenatalDetails.treatments);

    try {
      const response = await axios.request({
        url: `${API_BASE}/api/antenatal/${isEditMode ? 'update' : 'create'}`,
        method: isEditMode ? 'PUT' : 'POST',
        data: formData,
        headers: {
            'Content-Type': 'multipart/form-data',
            ...getAuthHeader()
        }      });
      if (response.status === 200 || response.status === 201) {
        // Submission successful
        history.push(isEditMode ? `/patients/view/${patientId}` : '/patients');
      } else {
        // Handle error response
        console.error('Error submitting antenatal details:', response.data);
        setError(response.data?.message || 'Could not save antenatal details. Please try again.');
      }
    } catch (err) {
      console.error('Error submitting antenatal details:', err);
      setError(err.response?.data?.message || 'Could not save antenatal details. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Handle input changes
  const handleChange = (event) => {
    const { name, value } = event.target;
    // Update state based on input field name
    if (name.startsWith('investigations')) {

        const [, nestedCategory, nestedProperty] = name.split('.');

        // Check if the input is for documents
        if (nestedProperty === 'details') {
          // Update the state for details of the investigation
          setAntenatalDetails((prevState) => ({
            ...prevState,
            investigations: {
              ...prevState.investigations,
              [nestedCategory]: {
                ...prevState.investigations[nestedCategory],
                [nestedProperty]: value
              }
            }
          }));
        }
    } else if (name.startsWith('medicalComplications')) {
      const [category, subCategory] = name.split('.');
      setAntenatalDetails((prevState) => ({
        ...prevState,
        [category]: {
          ...prevState.medicalComplications,
          [subCategory]: value
        }
      }));
    } else  if (name.startsWith('specificHistory')) {
        const [category, subCategory] = name.split('.');

        setAntenatalDetails((prevState) => ({
          ...prevState,
           [category]: {
            ...prevState.specificHistory,
            [subCategory]: value
          }
        }));
      } else if (name.startsWith('obstetricHistory')) {
        const [category, subCategory] = name.split('.');
        setAntenatalDetails((prevState) => ({
          ...prevState,
          [category]: {
            ...prevState.obstetricHistory,
            [subCategory]: value
          }
        }));
      } else if (name === 'LMP') {
        setAntenatalDetails((prevState) => ({
          ...prevState,
          LMP: value,
          expectedDateOfDelivery: eddEdited.current ? prevState.expectedDateOfDelivery : eddFromLmp(value)
        }));
      } else if (name === 'expectedDateOfDelivery') {
        // A hand-entered date wins over the calculation. Clearing it, or
        // typing exactly the calculated date, hands control back to the LMP.
        eddEdited.current = Boolean(value) && value !== eddFromLmp(antenatalDetails.LMP);
        setAntenatalDetails((prevState) => ({ ...prevState, expectedDateOfDelivery: value }));
      } else {
      setAntenatalDetails((prevState) => ({
        ...prevState,
        [name]: value
      }));
    }
  };
  const handleFileChange = (event) => {
    const { name, files } = event.target;
    const newDocuments = Array.from(files).map((file) => ({ name: file.name, file }));

    const [category, subCategory] = name.split('.');

    const [nestedCategory] = subCategory.split('.');
    // Check if the input is for documents
    if (category === 'investigations') {
      // Update the state to append new documents to the specified investigation
      setAntenatalDetails((prevState) => ({
        ...prevState,
        [category]: {
          ...prevState.investigations,
          [nestedCategory]: {
            ...prevState.investigations[nestedCategory],
            documents: [...prevState.investigations[nestedCategory].documents, ...newDocuments]
          }
        }
      }));
    }
  };

  const removeDocument = (index, name) => {
    const updatedDocuments =  antenatalDetails.investigations[name].documents;
    updatedDocuments.splice(index, 1);
    setAntenatalDetails((prevState) => ({
        ...prevState,
       investigations: {
          ...prevState.investigations,
          [name]: {
            ...prevState.investigations[name],
            documents: updatedDocuments
          }
        }
      }));
  };

  if (loading) {
    return (
      <div>
        <AppNavbar role={sessionStorage.getItem('userRole')} />
        <div className="background-container">
          <Spinner fullPage label="Loading..." />
        </div>
      </div>
    );
  }

  const renderObstetricSection = () => (
    <>
      <h3 className="record-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>Obstetric History</h3>
      <div className="patient-form-grid">
        <Field label="Gravida (total pregnancies)" htmlFor="obGravida">
          <input
            className="ui-input"
            type="number"
            min="0"
            id="obGravida"
            name="obstetricHistory.gravida"
            value={antenatalDetails.obstetricHistory.gravida}
            onChange={handleChange}
          />
        </Field>
        <Field label="Para (viable deliveries)" htmlFor="obPara">
          <input
            className="ui-input"
            type="number"
            min="0"
            id="obPara"
            name="obstetricHistory.para"
            value={antenatalDetails.obstetricHistory.para}
            onChange={handleChange}
          />
        </Field>
        <Field label="Abortus (losses)" htmlFor="obAbortus">
          <input
            className="ui-input"
            type="number"
            min="0"
            id="obAbortus"
            name="obstetricHistory.abortus"
            value={antenatalDetails.obstetricHistory.abortus}
            onChange={handleChange}
          />
        </Field>
        <Field label="Living (children alive)" htmlFor="obLiving">
          <input
            className="ui-input"
            type="number"
            min="0"
            id="obLiving"
            name="obstetricHistory.living"
            value={antenatalDetails.obstetricHistory.living}
            onChange={handleChange}
          />
        </Field>
      </div>

      <Field label="Last Menstrual Period (LMP)" required htmlFor="LMP">
        <DateInput
          id="LMP"
          name="LMP"
          value={antenatalDetails.LMP}
          onChange={handleChange}
          required
        />
      </Field>

      <Field label="Expected Date of Delivery" required htmlFor="expectedDateOfDelivery">
        <DateInput
          id="expectedDateOfDelivery"
          name="expectedDateOfDelivery"
          value={antenatalDetails.expectedDateOfDelivery}
          onChange={handleChange}
          required
        />
        <p className="text-muted" style={{ fontSize: '0.78rem', marginTop: 4 }}>
          Fills in automatically as LMP + 40 weeks. You can change it, for example after a dating scan.
        </p>
      </Field>

      <Field label="Pregnancy Complications" required htmlFor="pregnancyComplications">
        <input
          className="ui-input"
          type="text"
          id="pregnancyComplications"
          name="specificHistory.pregnancyComplications"
          value={antenatalDetails.specificHistory.pregnancyComplications}
          onChange={handleChange}
          required
        />
      </Field>

      <Field label="Previous Delivery By" htmlFor="previousDeliveryBy">
        <Select
          id="previousDeliveryBy"
          name="specificHistory.previousDeliveryBy"
          value={antenatalDetails.specificHistory.previousDeliveryBy}
          onChange={handleChange}
        >
          <option value="">Select Previous Delivery By</option>
          <option value="Normal">Normal</option>
          <option value="Caesarean">Caesarean</option>
          <option value="Ventouse">Ventouse</option>
          <option value="Others">Others</option>
        </Select>
      </Field>
    </>
  );

  const renderMedicalSection = () => (
    <>
      <h3 className="record-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>Medical History</h3>
      <Field label="Heart Disease" htmlFor="heartDisease">
        <textarea
          className="ui-textarea"
          id="heartDisease"
          name="medicalComplications.heartDisease"
          rows={2}
          value={antenatalDetails.medicalComplications.heartDisease}
          onChange={handleChange}
        />
      </Field>

      <Field label="Liver Disease" htmlFor="liverDisease">
        <textarea
          className="ui-textarea"
          id="liverDisease"
          name="medicalComplications.liverDisease"
          rows={2}
          value={antenatalDetails.medicalComplications.liverDisease}
          onChange={handleChange}
        />
      </Field>

      <Field label="Gastrointestinal Tract (GIT) Disease" htmlFor="GIT">
        <textarea
          className="ui-textarea"
          id="GIT"
          name="medicalComplications.GIT"
          rows={2}
          value={antenatalDetails.medicalComplications.GIT}
          onChange={handleChange}
        />
      </Field>

      <Field label="Kidney Disease" htmlFor="Kidney">
        <textarea
          className="ui-textarea"
          id="Kidney"
          name="medicalComplications.Kidney"
          rows={2}
          value={antenatalDetails.medicalComplications.Kidney}
          onChange={handleChange}
        />
      </Field>

      <Field label="Spine Problem" htmlFor="SpineProblem">
        <textarea
          className="ui-textarea"
          id="SpineProblem"
          name="medicalComplications.SpineProblem"
          rows={2}
          value={antenatalDetails.medicalComplications.SpineProblem}
          onChange={handleChange}
        />
      </Field>

      <Field label="Other Medical Complications" htmlFor="Others">
        <textarea
          className="ui-textarea"
          id="Others"
          name="medicalComplications.Others"
          rows={2}
          value={antenatalDetails.medicalComplications.Others}
          onChange={handleChange}
        />
      </Field>
    </>
  );

  const renderInvestigationsSection = () => (
    <>
      <h3 className="record-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>Investigations</h3>
      <InvestigationField
        label="Blood Investigation"
        id="bloodInvestigation"
        name="investigations.bloodInvestigation.details"
        fileFieldName="investigations.bloodInvestigation.documents"
        value={antenatalDetails.investigations.bloodInvestigation.details}
        onChange={handleChange}
        documents={antenatalDetails.investigations.bloodInvestigation.documents}
        onFileChange={handleFileChange}
        onRemoveDocument={(index) => removeDocument(index, 'bloodInvestigation')}
      />
      <InvestigationField
        label="Urine Investigation"
        id="urineInvestigation"
        name="investigations.urineInvestigation.details"
        fileFieldName="investigations.urineInvestigation.documents"
        value={antenatalDetails.investigations.urineInvestigation.details}
        onChange={handleChange}
        documents={antenatalDetails.investigations.urineInvestigation.documents}
        onFileChange={handleFileChange}
        onRemoveDocument={(index) => removeDocument(index, 'urineInvestigation')}
      />
      <InvestigationField
        label="Ultrasound Investigation"
        id="ultrasoundInvestigation"
        name="investigations.ultrasoundInvestigation.details"
        fileFieldName="investigations.ultrasoundInvestigation.documents"
        value={antenatalDetails.investigations.ultrasoundInvestigation.details}
        onChange={handleChange}
        documents={antenatalDetails.investigations.ultrasoundInvestigation.documents}
        onFileChange={handleFileChange}
        onRemoveDocument={(index) => removeDocument(index, 'ultrasoundInvestigation')}
      />
      <InvestigationField
        label="X-ray Investigation"
        id="xrayInvestigation"
        name="investigations.xrayInvestigation.details"
        fileFieldName="investigations.xrayInvestigation.documents"
        value={antenatalDetails.investigations.xrayInvestigation.details}
        onChange={handleChange}
        documents={antenatalDetails.investigations.xrayInvestigation.documents}
        onFileChange={handleFileChange}
        onRemoveDocument={(index) => removeDocument(index, 'xrayInvestigation')}
      />
    </>
  );

  const renderTreatmentsSection = () => (
    <>
      <h3 className="record-section-title" style={{ marginTop: 0, paddingTop: 0, borderTop: 'none' }}>Treatments</h3>
      <Field label="Treatments" htmlFor="treatments">
        <textarea
          className="ui-textarea"
          id="treatments"
          name="treatments"
          rows={4}
          value={antenatalDetails.treatments}
          onChange={handleChange}
        />
      </Field>
    </>
  );

  if (isEditMode) {
    return (
      <div>
        <AppNavbar role={sessionStorage.getItem('userRole')} />
        <div className="background-container">
          <Card variant="elevated" style={{ width: '100%', maxWidth: 680 }}>
            <IconBadge name="baby" />
            <span className="ui-eyebrow">Patient Records</span>
            <h2 className="section-title">Edit Antenatal Details</h2>
            {error && <div className="ui-banner ui-banner-error" ref={errorBannerRef}>{error}</div>}
            <Tabs tabs={ANTENATAL_TABS} active={editTab} onChange={setEditTab} />
            <form onSubmit={handleSubmit}>
              <input type="hidden" name="patientId" value={patientId} />
              {showSection('obstetric') && renderObstetricSection()}
              {showSection('medical') && renderMedicalSection()}
              {showSection('investigations') && renderInvestigationsSection()}
              {showSection('treatments') && renderTreatmentsSection()}
              <div className="case-form-actions">
                <Button type="submit" disabled={saving}>{saving ? 'Saving...' : 'Save Changes'}</Button>
                <Link to="/patients"><Button type="button" variant="ghost">Cancel</Button></Link>
              </div>
            </form>
          </Card>
        </div>
      </div>
    );
  }

  return (
    <div>
      <AppNavbar role={sessionStorage.getItem('userRole')} />
      <div className="background-container">
        <div className="wizard-layout">
          <div className="wizard-tracker-col">
            <StepTracker steps={ANTENATAL_TABS} active={wizardStep} furthestStep={furthestStep} onChange={jumpToStep} />
          </div>
          <Card variant="elevated" className="wizard-card">
            <IconBadge name="baby" />
            <span className="ui-eyebrow">Patient Records</span>
            <h2 className="section-title">Antenatal Details Form</h2>
            {error && <div className="ui-banner ui-banner-error" ref={errorBannerRef}>{error}</div>}
            <form ref={formRef} onSubmit={handleSubmit}>
              <input type="hidden" name="patientId" value={patientId} />
              {showSection('obstetric') && renderObstetricSection()}
              {showSection('medical') && renderMedicalSection()}
              {showSection('investigations') && renderInvestigationsSection()}
              {showSection('treatments') && renderTreatmentsSection()}

              <div className="wizard-nav">
                <Button type="button" variant="ghost" onClick={goBack} disabled={wizardStepIndex === 0}>
                  <Icon name="arrow-left" size={16} /> Previous
                </Button>
                {/* Real bug found live (same root cause on AddPatient.js's
                    own wizard): a type="submit" button on the last step
                    lets React silently flip the SAME clicked DOM node from
                    type="button" to "submit" mid-click as it re-renders past
                    the step boundary, so the browser's native default
                    action then submits the form a step early. Always
                    type="button"; the last step calls handleSubmit directly. */}
                <Button type="button" onClick={isLastWizardStep ? () => handleSubmit() : goNext} disabled={saving}>
                  {isLastWizardStep ? (saving ? 'Saving...' : 'Submit Antenatal Details') : 'Next'}
                </Button>
              </div>
            </form>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default AntenatalDetailsForm;
